import React, { useContext, useEffect, useState } from "react";

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AuthContext from "../../context/auth/authContext";
import AlertContext from "../../context/alert/alertContext";
import SpotReviewContext from "../../context/spot/spotReviewContext";
import TripContext from "../../context/trip/tripContext";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { getRelationDisplay } from "../../third_parties/map/placeAutoComplete";
import SpotReviewPreview from '../spots/SpotReviewPreview';
import SpotReviewComposerModal from '../spots/SpotReviewComposer/SpotReviewComposerModal';

const getBackLabel = (from, { spotName, tripTitle } = {}) => {
  if (from === '/my-journeys') return 'Back to My Journeys';
  if (from === '/spots') return 'Back to Local Finds';
  if (from === '/destinations') return 'Back to Discover';
  if (from.startsWith('/destination/')) return 'Back to Trips';
  if (from.startsWith('/spot/')) return spotName ? `Back to ${spotName}` : 'Back to Spot';
  if (from.startsWith('/trip/')) return tripTitle ? `Back to ${tripTitle}` : 'Back to Trip';
  return 'Back';
};

const SpotReviewDetail = () => {
  const authContext = useContext(AuthContext);
  const { loadUser } = authContext;
  const { review_id } = useParams();
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const from = params.get('from') || '/spots';

  const alertContext = useContext(AlertContext);
  const { setAlert } = alertContext;

  const spotContext = useContext(SpotReviewContext);
  const { spots, spotReviews, userReviews, getSpotReview, getSubReviews, deleteSpotReview, spotReviewAdded } = spotContext;

  const tripContext = useContext(TripContext);
  const { tripDetail } = tripContext;

  // Resolve a friendlier back label: "Back to <spot name>" or "Back to <trip title>".
  const spotMatch = from.match(/^\/spot\/(\d+)/);
  const cachedSpotName = spotMatch && spots
    ? (spots.find((s) => String(s.id) === spotMatch[1]) || {}).name
    : null;
  const tripMatch = from.match(/^\/trip\/(\d+)/);
  const tripTitle = tripMatch && tripDetail && String(tripDetail.id) === tripMatch[1]
    ? tripDetail.title
    : null;

  // Try to find the review from existing state.
  const tripSpotReviews = tripDetail && tripDetail.spot_reviews ? tripDetail.spot_reviews : [];
  const review =
    (spotReviews && spotReviews.find((r) => String(r.id) === String(review_id))) ||
    (userReviews && userReviews.find((r) => String(r.id) === String(review_id))) ||
    tripSpotReviews.find((r) => String(r.id) === String(review_id)) ||
    null;

  const [subReviews, setSubReviews] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const currentUserID = authContext && authContext.user && authContext.user.user_id;
  const isOwner = !!(currentUserID && review && review.owner_user_id === currentUserID);

  const handleDelete = async () => {
    if (!window.confirm('Delete this review?')) return;
    const result = await deleteSpotReview(review_id);
    if (result.ok) {
      setAlert("Review deleted", "success");
      history.push(from);
    } else {
      setAlert(result.msg || "Failed to delete review", "error");
    }
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!review) {
      getSpotReview(review_id);
    }
    // eslint-disable-next-line
  }, [review_id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await getSubReviews(review_id);
      if (!cancelled) setSubReviews(list || []);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [review_id, spotReviewAdded]);

  if (!review) {
    const loadingLabel = getBackLabel(from, { spotName: cachedSpotName, tripTitle });
    return (
      <Box sx={{ width: '100%', pt: 8 }}>
        <Button
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          onClick={() => history.push(from)}
          sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500, mb: 2 }}
        >
          {loadingLabel}
        </Button>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  const { relation, parent_id, parent_title, spot_name } = review;
  const relationDisplay = getRelationDisplay(relation);
  const isSubReview = !!relation;

  const backLabel = getBackLabel(from, {
    spotName: cachedSpotName || spot_name,
    tripTitle,
  });

  return (
    <Box sx={{ width: '100%', pt: 8 }}>
      <Button
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
        onClick={() => history.push(from)}
        sx={{
          textTransform: 'none',
          color: 'text.secondary',
          fontWeight: 500,
          mb: 2,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {backLabel}
      </Button>

      {isSubReview && parent_id && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {relationDisplay && (
            <Chip
              size="small"
              icon={relationDisplay.icon}
              label={relationDisplay.label}
              sx={{ fontWeight: 500 }}
            />
          )}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            for{' '}
            <Button
              variant="text"
              size="small"
              onClick={() => history.push(`/review/${parent_id}?from=${encodeURIComponent(from)}`)}
              sx={{ textTransform: 'none', p: 0, minWidth: 0, fontWeight: 600 }}
            >
              {parent_title || 'parent review'}
            </Button>
          </Typography>
        </Box>
      )}

      <SpotReviewPreview
        review={review}
        subReviews={isSubReview ? [] : subReviews}
        onSubClick={(sub) => history.push(
          `/review/${sub.id}?from=${encodeURIComponent(`/review/${review_id}`)}`
        )}
        isOwner={isOwner}
        onEdit={() => setEditModalOpen(true)}
        onDelete={handleDelete}
      />

      {isOwner && (
        <SpotReviewComposerModal
          editReview={review}
          externalOpen={editModalOpen}
          onExternalClose={() => {
            setEditModalOpen(false);
            getSpotReview(review_id);
          }}
        />
      )}
    </Box>
  );
};

export default SpotReviewDetail;
